import { Button, styled, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useCallback } from 'react';
import * as yup from 'yup';
import { FormValidationMessage } from '../../../../components/FormValidationMessage/FormValidationMessage';
import { StatusBadge } from '../../../../components/StatusBadge/StatusBadge';
import { Typography } from '../../../../components/Typography/Typography';
import { useLoadingBar } from '../../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { roleLabel } from '../../../../utils/roleLabels';
import { firstRole, rolesOf } from '../../../../utils/roles';
import { useEditUser } from '../../../../queries/hooks/useEditUser';
import { EditUserPayload, LoadingId, SelectItem, USER_COUNTRY, User, UserRole } from '../../../../types';
import { DealOwnerFields } from './DealOwnerFields';
import { OPManagerFields } from './OPManagerFields';
import { OperatingPartnerFields } from './OperatingPartnerFields';
import { ProxyFields } from './ProxyFields';
import { CollapsibleRoleSection } from './CollapsibleRoleSection';
import { RolesCardPicker } from './RolesCardPicker';
import { DeactivateSection } from './DeactivateSection';
import { UserModalShell } from './UserModalShell';
import { WorkflowField } from './WorkflowField';
import { useWorkflows } from '../../../../queries/hooks/useWorkflows';
import { WorkflowStage } from '../../../../types';
import { createAddUserPayload, createEditUserPayload } from './helpers';
import { isEndDateInvalid as isUserEndDateInvalid } from './isEndDateInvalid';
import { BasicUserFields } from './BasicUserFields';
import { useAddUser } from '../../../../queries/hooks/useAddUser';
import { useAtomValue } from 'jotai';
import { userState } from '../../../../state/UIState';
import { useOktaAuth } from '@okta/okta-react';

type Props = {
  userProfileOpen: boolean;
  handleUserProfilClose: () => void;
  user?: User;
};

export interface IUserFileds {
  name: string;
  email: string;
  // Multi-role source of truth. Legacy role/isOp/isAdmin are derived from this on the backend.
  roles: UserRole[];
  isAssetServices: boolean;
  workflow?: SelectItem | null;
  managers?: SelectItem[];
  svpManagers?: SelectItem[];
  dealOwnerDeals?: SelectItem[];
  operatingPartnerDeals?: SelectItem[];
  operatingPartners?: SelectItem[];
  proxyOperatingPartners?: SelectItem[];
  /** SVP-820: ids of the assigned submitters this proxy may submit for (subset of the list above). */
  proxyCanSubmitFor?: number[];
  proxy?: SelectItem | null;
  startDate?: string;
  endDate?: string;
  country?: USER_COUNTRY;
}

/** Any submitter: holds the Operating Partner role (timesheets are generated for these users). */
const isSubmitterRole = (roles?: UserRole[]): boolean =>
  !!roles && roles.includes(UserRole.OPERATING_PARTNER);

export function UserModal({ userProfileOpen, handleUserProfilClose, user }: Props) {
  const loggedUser = useAtomValue(userState);
  const { oktaAuth } = useOktaAuth();
  const { colors } = useTheme();

  const { pushSuccessToast, pushErrorToast } = useToastMessage();

  const { startLoading, stopLoading } = useLoadingBar();

  const { mutate: onEditUser, isLoading: editInProgress } = useEditUser();
  const { mutate: onAddUser, isLoading: addInProgress } = useAddUser();
  const { data: workflows } = useWorkflows();

  const isDeactivated = user?.isActive === false;

  const handleDeactivate = useCallback(() => {
    if (!user) return;
    startLoading(LoadingId.editUser);
    const payload: EditUserPayload = { deactivate: true };
    onEditUser(
      { payload, id: user.id },
      {
        onSuccess: () => {
          pushSuccessToast({
            title: 'User Deactivated',
            message: `${user.name} has been deactivated.`,
          });
          handleUserProfilClose();
        },
        onError: () => {
          pushErrorToast({ message: 'Failed to deactivate user' });
        },
        onSettled: () => {
          stopLoading(LoadingId.editUser);
        },
      }
    );
  }, [user, onEditUser, startLoading, stopLoading, pushSuccessToast, pushErrorToast, handleUserProfilClose]);

  const handleActivate = useCallback(() => {
    if (!user) return;
    startLoading(LoadingId.editUser);
    const payload: EditUserPayload = { activate: true };
    onEditUser(
      { payload, id: user.id },
      {
        onSuccess: () => {
          pushSuccessToast({
            title: 'User Activated',
            message: `${user.name} has been activated.`,
          });
          handleUserProfilClose();
        },
        onError: () => {
          pushErrorToast({ message: 'Failed to activate user' });
        },
        onSettled: () => {
          stopLoading(LoadingId.editUser);
        },
      }
    );
  }, [user, onEditUser, startLoading, stopLoading, pushSuccessToast, pushErrorToast, handleUserProfilClose]);

  const initialValues = useMemo(() => {
    // SVP-826: a self-proxy can only be stale data from before the pickers excluded the edited user.
    // Dropping it here keeps the form consistent with the options AND lets the next save clean it up,
    // rather than re-sending a value the API now refuses.
    const proxyDefaultValue =
      user?.proxy && user.proxy.id !== user.id
        ? ({ id: user.proxy.id, value: user?.proxy.name } as SelectItem)
        : null;
    const proxySubmitters = (user?.proxyOperatingPartners || []).filter(
      (partner) => partner.email !== user?.email
    );
    return {
      name: user?.name || '',
      email: user?.email || '',
      roles: rolesOf(user),
      isAssetServices: Boolean(user?.isAssetServices),
      workflow: user?.workflow ? { id: user.workflow.id, value: user.workflow.name } : null,
      managers: (user?.operatingPartnerManagers || [])
        .filter((manager) => manager.email !== user?.email)
        .map((manager) => ({
          id: manager?.id,
          value: manager?.name,
        })),
      svpManagers: (user?.svpManagers || [])
        .filter((svpManager) => svpManager.email !== user?.email)
        .map((svpManager) => ({
          id: svpManager?.id,
          value: svpManager?.name,
        })),
      dealOwnerDeals: (user?.dealOwnerDeals || []).map((deal) => ({
        id: deal?.id,
        value: deal?.name,
      })),
      operatingPartnerDeals: (user?.operatingPartnerDeals || []).map((deal) => ({
        id: deal?.id,
        value: deal?.name,
      })),
      proxyOperatingPartners: proxySubmitters.map((partner) => ({
        id: partner?.id,
        value: partner?.name,
      })),
      // SVP-820: these are by definition the submitters pointing at THIS proxy, so their stored flag is
      // unambiguous here. A submitter added to the list in this session starts out switched off.
      proxyCanSubmitFor: proxySubmitters
        .filter((partner) => partner?.proxyCanSubmit)
        .map((partner) => partner.id),
      operatingPartners: (user?.operatingPartners || [])
        .filter((partner) => partner.email !== user?.email)
        .map((partner) => ({
          id: partner?.id,
          value: partner?.name,
        })),
      proxy: proxyDefaultValue,
      startDate: user?.startDate,
      endDate: user?.endDate,
      country: user?.country,
    };
  }, [user]);

  const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    email: yup.string().email().required('Email is required'),
    roles: yup.array().min(1, 'At least one role is required'),
    // Workflow is required for any submitter (a user holding the Operating Partner role).
    workflow: yup.mixed().when('roles', ([roles]) => {
      const isSubmitter = ((roles as UserRole[]) ?? []).includes(UserRole.OPERATING_PARTNER);
      if (isSubmitter) {
        return yup.mixed().test('workflow-required', 'Workflow is required', (value) => !!value);
      }
      return yup.mixed().nullable();
    }),
    // SVP-811 L5: the Managers picker is only rendered for submitters (holders of Operating Partner).
    // Removing the Submitter role hides the picker but does NOT clear the `workflow` value, so gating
    // the requirement on workflow alone fires "Manager is required" on a hidden field and blocks the
    // save. Require managers only while the user is still a submitter AND the workflow has a Manager stage.
    managers: yup.mixed().when(['roles', 'workflow'], ([roles, workflow]) => {
      const isSubmitter = ((roles as UserRole[]) ?? []).includes(UserRole.OPERATING_PARTNER);
      const selected = workflows?.find((w) => w.id === Number((workflow as SelectItem | null)?.id));
      const hasManagerStage = (selected?.approvalStages ?? []).includes(WorkflowStage.MANAGER);
      if (isSubmitter && hasManagerStage) {
        return yup.array().min(1, 'Manager is required');
      }
      return yup.mixed().nullable();
    }),
    dealOwnerDeals: yup.mixed().nullable(),
    // Start Date and Country are required for ANY submitter (holds Operating Partner): Start Date
    // drives timesheet generation and Country drives holiday matching, so a submitter missing either
    // ends up with no generated timesheets / no holidays. Deals stay optional.
    operatingPartnerDeals: yup.mixed().nullable(),
    startDate: yup.mixed().when('roles', ([roles]) => {
      if (isSubmitterRole(roles as UserRole[])) return yup.mixed().required('Start Date is required');
      return yup.mixed().nullable();
    }),
    country: yup.mixed().when('roles', ([roles]) => {
      if (isSubmitterRole(roles as UserRole[])) return yup.mixed().required('Country is required');
      return yup.mixed().nullable();
    }),
    proxy: yup.mixed().nullable(),
    operatingPartners: yup.mixed().nullable(),
    proxyOperatingPartners: yup.mixed().nullable(),
    proxyCanSubmitFor: yup.mixed().nullable(),
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      validateOnBlur
      initialValues={initialValues}
      validateOnMount
      validateOnChange
      onSubmit={(values) => {
        if (user) {
          startLoading(LoadingId.editUser);
          const payload = createEditUserPayload(values);
          onEditUser(
            {
              payload,
              id: user.id,
            },
            {
              onSuccess: () => {
                pushSuccessToast({
                  title: 'Profile Updated',
                  message: `${user?.name} ${roleLabel(firstRole({ roles: values.roles }))} Profile has been successfully updated.`,
                });
                const adminChanged =
                  initialValues.roles.includes(UserRole.ADMIN) !== values.roles.includes(UserRole.ADMIN);
                if (loggedUser?.email === user.email && adminChanged) {
                  oktaAuth.signOut();
                }
                handleUserProfilClose();
              },
              onError: (error: unknown) => {
                pushErrorToast({ message: extractErrorMessage(error) });
              },
              onSettled: () => {
                stopLoading(LoadingId.editUser);
              },
            }
          );
        } else {
          startLoading(LoadingId.addUser);
          const payload = createAddUserPayload(values);
          onAddUser(
            {
              payload,
            },
            {
              onSuccess: () => {
                pushSuccessToast({
                  title: 'User Added',
                  message: `${values.name} ${roleLabel(firstRole({ roles: values.roles }))} User has been successfully added.`,
                });
                handleUserProfilClose();
              },
              onError: (error: unknown) => {
                pushErrorToast({ message: extractErrorMessage(error) });
              },
              onSettled: () => {
                stopLoading(LoadingId.addUser);
              },
            }
          );
        }
      }}
    >
      {({ handleSubmit, errors, values, validateForm, initialValues, dirty }) => {
        const hasValidationErrors =
          !!Object.keys(errors)?.length ||
          isUserEndDateInvalid({
            endDate: values.endDate,
            startDate: values.startDate,
            initialEndDate: initialValues.endDate,
          });

        // Only the Submitter section owns validated fields; keep it open when it has errors so the user
        // can see what is missing even if they collapsed it.
        const submitterErrorKeys = ['workflow', 'managers', 'startDate', 'country', 'operatingPartnerDeals'];
        const submitterHasError = submitterErrorKeys.some(
          (key) => !!(errors as Record<string, unknown>)[key]
        );

        const names = (items?: SelectItem[]) => (items ?? []).map((item) => item.value).join(', ');
        const countNoun = (n: number, singular: string, plural: string) =>
          `${n} ${n === 1 ? singular : plural}`;

        const managerSummary = values.operatingPartners?.length
          ? `Approves for ${countNoun(values.operatingPartners.length, 'submitter', 'submitters')}: ${names(values.operatingPartners)}`
          : 'No submitters assigned yet';
        // Populating is what the role always does; submitting is the opt-in granted per submitter, so
        // it is only mentioned when at least one switch is actually on.
        const proxyCanSubmitCount = (values.proxyCanSubmitFor ?? []).length;
        const proxySummary = values.proxyOperatingPartners?.length
          ? `Populates for ${countNoun(values.proxyOperatingPartners.length, 'submitter', 'submitters')}: ${names(values.proxyOperatingPartners)}${
              proxyCanSubmitCount ? ` · can submit for ${proxyCanSubmitCount}` : ''
            }`
          : 'No submitters assigned yet';
        const dealOwnerSummary = values.dealOwnerDeals?.length
          ? `Owns ${countNoun(values.dealOwnerDeals.length, 'deal', 'deals')}: ${names(values.dealOwnerDeals)}`
          : 'No deals assigned yet';

        // Roles that carry no extra configuration are shown together in one static row.
        const noSettingRoles = [UserRole.ACCOUNTANT, UserRole.ADMIN].filter((role) =>
          values.roles.includes(role)
        );

        const avatarInitials =
          (user?.name || '')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((word) => word[0]?.toUpperCase())
            .join('') || 'U';

        const header = user ? (
          <>
            <Avatar>{avatarInitials}</Avatar>
            <HeaderNameWrap>
              <Typography variant='h4' color={colors.text.main} style={{ lineHeight: 1.2 }}>
                {user.name}
              </Typography>
              <StatusBadge isActive={user.isActive !== false} />
            </HeaderNameWrap>
          </>
        ) : (
          <Typography variant='h4' color={colors.text.main}>
            Add User
          </Typography>
        );

        const footer = (
          <>
            <UnsavedWrap>
              {dirty && !isDeactivated && (
                <>
                  <UnsavedDot />
                  <Typography variant='caption' color={colors.text.secondary}>
                    Unsaved changes
                  </Typography>
                </>
              )}
            </UnsavedWrap>
            <FooterButtons>
              <Button variant='outlined' onClick={handleUserProfilClose} style={{ height: 44 }}>
                Cancel
              </Button>
              <Button
                variant='contained'
                disabled={editInProgress || addInProgress || hasValidationErrors || isDeactivated}
                onClick={() => handleSubmit()}
                style={{ height: 44 }}
              >
                {user ? 'Save Changes' : 'Add User'}
              </Button>
            </FooterButtons>
          </>
        );

        return (
          <Form style={{ display: 'contents' }}>
            <UserModalShell
              isOpen={userProfileOpen}
              onClose={handleUserProfilClose}
              header={header}
              footer={footer}
              width='640px'
              top='48px'
            >
              <Body style={isDeactivated ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
                <BasicUserFields disableFields={!!user || isDeactivated} />
                <RolesCardPicker validateForm={validateForm} />

                {values.roles.length > 0 && (
                  <RoleSettings>
                    <Typography
                      variant='overline'
                      color={colors.text.caption}
                      style={{ letterSpacing: '0.08em' }}
                    >
                      ROLE SETTINGS
                    </Typography>

                    {values.roles.includes(UserRole.OPERATING_PARTNER) && (
                      <CollapsibleRoleSection
                        title={roleLabel(UserRole.OPERATING_PARTNER)}
                        defaultOpen
                        forceOpen={submitterHasError}
                      >
                        <WorkflowField />
                        <OperatingPartnerFields />
                      </CollapsibleRoleSection>
                    )}
                    {values.roles.includes(UserRole.OP_MANAGER) && (
                      <CollapsibleRoleSection title={roleLabel(UserRole.OP_MANAGER)} summary={managerSummary}>
                        <OPManagerFields />
                      </CollapsibleRoleSection>
                    )}
                    {values.roles.includes(UserRole.PROXY) && (
                      <CollapsibleRoleSection title={roleLabel(UserRole.PROXY)} summary={proxySummary}>
                        <ProxyFields />
                      </CollapsibleRoleSection>
                    )}
                    {values.roles.includes(UserRole.DEAL_OWNER) && (
                      <CollapsibleRoleSection title={roleLabel(UserRole.DEAL_OWNER)} summary={dealOwnerSummary}>
                        <DealOwnerFields />
                      </CollapsibleRoleSection>
                    )}
                    {noSettingRoles.length > 0 && (
                      <CollapsibleRoleSection
                        collapsible={false}
                        title={noSettingRoles.map((role) => roleLabel(role)).join(', ')}
                        summary='No extra settings needed'
                      />
                    )}
                  </RoleSettings>
                )}

                {hasValidationErrors && !isDeactivated && (
                  <FormValidationMessage text='Please, enter the missing details to add a Team Member.' />
                )}
              </Body>

              {user && (
                <DeactivateSection
                  isActive={user.isActive !== false}
                  disabled={editInProgress}
                  onDeactivate={handleDeactivate}
                  onActivate={handleActivate}
                />
              )}
            </UserModalShell>
          </Form>
        );
      }}
    </Formik>
  );
}

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RoleSettings = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Avatar = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.surfaceAccent.default};
  color: ${({ theme }) => theme.colors.textAccent.inverse};
  font-size: 14px;
  font-weight: 600;
`;

const HeaderNameWrap = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
`;

const UnsavedWrap = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const UnsavedDot = styled('span')`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e5a000;
  flex-shrink: 0;
`;

const FooterButtons = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;
